import mqtt from "mqtt";
import { useState, useEffect, useRef } from "react";

export default function useMQTTService(topic, deviceId) {
    const [client, setClient] = useState(null);
    const [reconnecting, setReconnecting] = useState(false);
    const [reconnected, setReconnected] = useState(false);
    const reconnectingRef = useRef(false);
    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => {
        // No conectar hasta que tengamos el topic y deviceId
        if (!topic || !deviceId) {
            console.log('⏳ MQTT: Waiting for topic and deviceId before connecting...');
            return;
        }

        const brokerUrl = process.env.REACT_APP_MQTT_WS_URL;
        const username = process.env.REACT_APP_MQTT_USER;
        const password = process.env.REACT_APP_MQTT_PASSWORD;

        if (!brokerUrl) {
            console.error('❌ MQTT: REACT_APP_MQTT_WS_URL not configured');
            return;
        }

        console.log('🔌 MQTT: Connecting to', brokerUrl, 'with user:', username);
        const mqttClient = mqtt.connect(brokerUrl, {
            username: username,
            password: password,
            clientId: `web_${Math.random().toString(16).substring(2, 10)}`,
            clean: true,
            reconnectPeriod: 5000,
            connectTimeout: 30000,
            keepalive: 30,
        });

        setClient(mqttClient);

        return () => {
            if (mqttClient) {
                console.log('🔌 MQTT: Disconnecting client');
                mqttClient.end();
            }
        };
    }, [topic, deviceId]);

    useEffect(() => {
        if (!topic || !deviceId || !client) {
            console.log('⏳ MQTT: Waiting for client and topic parameters...');
            return;
        }

        const fullTopic = `${topic}/${deviceId}`;
        console.log('📡 MQTT: Using topic:', fullTopic);

        const onConnect = () => {
            console.log('✅ MQTT: Connected to broker');
            console.log('📡 MQTT: Subscribing to topic:', fullTopic);
            if (reconnectingRef.current) {
                setReconnected(true);
                setTimeout(() => setReconnected(false), 5000);
            }
            reconnectingRef.current = false;
            setReconnecting(false);
            setLoaded(true);

            client.subscribe(fullTopic, { qos: 1 }, (err) => {
                if (err) {
                    console.error('❌ MQTT: Error subscribing to topic', err);
                } else {
                    console.log(`✅ MQTT: Successfully subscribed to: ${fullTopic}`);
                }
            });
        };

        const onMessage = (receivedTopic, message) => {
            console.log('📨 MQTT: Message received on topic:', receivedTopic);

            try {
                const parsed = JSON.parse(message.toString());
                console.log('✅ MQTT: Data received:', parsed);
                setData(parsed);
            } catch (e) {
                console.error('❌ MQTT: Error parsing message', e);
            }
        };

        const onError = (err) => {
            console.error('MQTT: Connection error', err);
            reconnectingRef.current = true;
            setReconnecting(true);
            setReconnected(false);
        };

        const onClose = () => {
            console.log('MQTT: Connection closed');
            reconnectingRef.current = true;
            setReconnecting(true);
            setReconnected(false);
        };

        const onReconnect = () => {
            console.log('MQTT: Reconnecting...');
            reconnectingRef.current = true;
            setReconnecting(true);
        };

        const onOffline = () => {
            console.log('MQTT: Client offline');
            reconnectingRef.current = true;
            setReconnecting(true);
        };

        client.on('connect', onConnect);
        client.on('message', onMessage);
        client.on('error', onError);
        client.on('close', onClose);
        client.on('reconnect', onReconnect);
        client.on('offline', onOffline);

        return () => {
            client.off('connect', onConnect);
            client.off('message', onMessage);
            client.off('error', onError);
            client.off('close', onClose);
            client.off('reconnect', onReconnect);
            client.off('offline', onOffline);

            if (fullTopic) {
                client.unsubscribe(fullTopic, (err) => {
                    if (err) {
                        console.error('MQTT: Error unsubscribing');
                    }
                });
            }
        };
    }, [client, topic, deviceId]);

    return {
        loaded,
        data,
        reconnecting,
        reconnected,
    };
}
